/**
 * Playing a lock form's receive (ท่าล็อกเป้า) on its target: from the beat the rules say it is in (systems/duelLock.ts
 * `poseOf`, carried to the renderer in `lockMark.pose`), which receive motion (render/rig/receive.ts) to draw, how far
 * through it, and what the shot does beside the pose (laid down, lifted, whirled round). Docs: docs/research/C0_RECEIVE.md.
 */
import { eased, type Ease } from '../motions/kit';
import { MOTIONS, RECEIVE_CHANNELS, type ReceiveMotionId } from './receive';

export interface ReceiveFrame {
  id: ReceiveMotionId;
  /** 0–1 through the motion. */
  u: number;
  /** Turn about the feet (radians, the figure's own frame: negative = over backwards). */
  rot: number;
  /** Art pixels off the ground. */
  lift: number;
  /** Heading steps (45°) the view is whirled round by. */
  turn: number;
}

const BEAT_MOTION: Readonly<Record<string, ReceiveMotionId>> = {
  hit: 'rcvHit',
  spin: 'rcvSpin',
  knock: 'rcvKnock',
  kneel: 'rcvKneel',
  fall: 'rcvFall',
};

/** By entity: the last beat before `recover`, which picks the way up (from a knee, from the back, else from a stagger). */
const before = new Map<number, string>();

/** A channel's value `u` (0–1) through a motion, blended between its keys with the motion's own eases. */
function channel(vals: readonly number[] | undefined, ease: readonly Ease[], u: number): number {
  if (!vals?.length) return 0;
  const n = vals.length;
  const x = Math.max(0, Math.min(1, u)) * (n - 1);
  const i = Math.min(n - 2, Math.floor(x));
  const t = eased(ease[i] ?? 'inOut', x - i);
  return vals[i]! + (vals[i + 1]! - vals[i]!) * t;
}

/** The receive frame for entity `id` in beat `kind`, `k` (0–1) of the way through the beat. */
export function receiveFrame(id: number, kind: string, k: number): ReceiveFrame {
  let mid: ReceiveMotionId;
  if (kind === 'recover') {
    const b = before.get(id);
    mid = b === 'fall' ? 'rcvUpKip' : b === 'kneel' ? 'rcvUpKneel' : 'rcvUp';
  } else {
    mid = BEAT_MOTION[kind] ?? 'rcvHit';
    before.set(id, kind);
    if (before.size > 256) before.delete(before.keys().next().value!);
  }
  const m = MOTIONS.find((x) => x.id === mid)!;
  const ch = RECEIVE_CHANNELS[mid];
  const u = Math.max(0, Math.min(1, k));
  return {
    id: mid,
    u,
    rot: channel(ch?.rot, m.ease, u),
    lift: channel(ch?.lift, m.ease, u),
    turn: Math.round(channel(ch?.turn, m.ease, u)),
  };
}
